import { AudioLevelMonitor } from "@/components/AudioLevelMonitor";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  AudioDevice,
  audioSettingsStorage,
  getAudioInputDevices,
} from "@/utils/audioSettings";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Label } from "./ui/label";

interface AudioDeviceSelectorProps {
  onDeviceSelected?: (device: AudioDevice) => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
  className?: string;
}

export const AudioDeviceSelector: React.FC<AudioDeviceSelectorProps> = ({
  onDeviceSelected,
  onCancel,
  showCancelButton = false,
  className,
}) => {
  const [devices, setDevices] = useState<AudioDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load devices on mount
  useEffect(() => {
    const loadDevices = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const audioDevices = await getAudioInputDevices();
        setDevices(audioDevices);

        // Auto-select the previously saved device if it exists
        const savedDevice = audioSettingsStorage.getSelectedDevice();
        if (savedDevice) {
          const deviceExists = audioDevices.find(
            (d) => d.deviceId === savedDevice.deviceId,
          );
          if (deviceExists) {
            setSelectedDeviceId(savedDevice.deviceId);
          }
        }

        // If no device selected and only one available, auto-select it
        if (!selectedDeviceId && audioDevices.length === 1) {
          setSelectedDeviceId(audioDevices[0].deviceId);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load audio devices",
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadDevices();
  }, []);

  const handleSaveDevice = useCallback(() => {
    if (!selectedDeviceId) {
      toast.error("Please select a microphone first");
      return;
    }

    const selectedDevice = devices.find((d) => d.deviceId === selectedDeviceId);
    if (!selectedDevice) {
      toast.error("Selected device not found");
      return;
    }

    // Save to localStorage
    audioSettingsStorage.setSelectedDevice(selectedDevice);

    toast.success(`Microphone "${selectedDevice.label}" saved successfully!`);

    // Call the callback
    onDeviceSelected?.(selectedDevice);
  }, [selectedDeviceId, devices, onDeviceSelected]);

  const handleRefresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const audioDevices = await getAudioInputDevices();
      setDevices(audioDevices);

      // If the currently selected device no longer exists, clear selection
      if (
        selectedDeviceId &&
        !audioDevices.find((d) => d.deviceId === selectedDeviceId)
      ) {
        setSelectedDeviceId("");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to refresh devices",
      );
    } finally {
      setIsLoading(false);
    }
  }, [selectedDeviceId]);

  if (isLoading) {
    return (
      <div className={cn("flex items-center space-x-2", className)}>
        <div className="w-4 h-4 border border-neutral-400 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-muted-foreground">
          Loading audio devices...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("text-center space-y-4", className)}>
        <p className="text-sm text-lc-blocked dark:text-lc-blocked">{error}</p>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      {devices.length === 0 ? (
        <div className="text-center space-y-4">
          <p className="text-sm text-muted-foreground">
            No microphones detected
          </p>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            Refresh Devices
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <Label>Available Microphones</Label>
            <Select
              value={selectedDeviceId}
              onValueChange={setSelectedDeviceId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a microphone..." />
              </SelectTrigger>
              <SelectContent>
                {devices.map((device) => (
                  <SelectItem key={device.deviceId} value={device.deviceId}>
                    {device.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedDeviceId && (
            <div className="space-y-3">
              {/* Always-visible audio level monitor */}
              <div className="space-y-2">
                <Label>Live Audio Levels</Label>
                <AudioLevelMonitor
                  device={
                    devices.find((d) => d.deviceId === selectedDeviceId) || null
                  }
                  height={60}
                />
                <p className="text-xs text-muted-foreground">
                  Speak into your microphone to see live audio levels and verify
                  your selection is working correctly.
                </p>
              </div>

              <div className="flex items-center space-x-2 pt-2">
                <Button onClick={handleSaveDevice} className="flex-1">
                  Save Selection
                </Button>

                {showCancelButton && (
                  <Button variant="outline" onClick={onCancel}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            className="w-full text-xs text-muted-foreground hover:text-foreground"
          >
            Refresh device list
          </Button>
        </>
      )}
    </div>
  );
};
